import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle
} from '@angular/material/dialog';
import { Component, inject } from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { ButtonComponent } from '../button/button.component';
import { MatFormField, MatInput, MatSuffix } from '@angular/material/input';
import { FormsModule } from '@angular/forms';
import { NgClass } from '@angular/common';
import { DialogTestIds } from './dialog.test-ids';
import { PasswordService } from '../../../services/password/password.service';
import { MatIconButton } from '@angular/material/button';
import { copyToClipboard } from '../../../services/util';

export interface DialogData {
  title: string;
  message: string;
  confirmText: string;
  cancelText?: string;
  svgIcon?: string;
  cancelDataTestId?: string;
  confirmDataTestId: string;
  dialogContentTestId: string;
  dialogActionTestId: string;
  subMessage?: string;
  needInput?: boolean;
  isLongMessage?: boolean;
  validationText?: string;
  inputValue?: string;
  showPasswordInput?: boolean;
  showGenerateButton?: boolean;
  inputPlaceholder?: string;
  requireCopy?: boolean;
  prefilledInput?: string;
  displayAsPlainText?: boolean;
  inputFieldTestId?: string;
  inputPlainHeader?: string;
}

@Component({
  selector: 'app-dialog',
  imports: [
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatIcon,
    ButtonComponent,
    MatInput,
    FormsModule,
    NgClass,
    MatFormField,
    MatDialogClose,
    MatSuffix,
    MatIconButton
  ],
  templateUrl: './dialog.component.html',
  styleUrl: './dialog.component.scss'
})

/**
 * Begründung: Hierbei handelt es sich um eine Allgemeione Dialogkomponente für Hinweise, welche an der jeweiligen
 * Stelle verändert und eingebaut werden kann.
 */
export class DialogComponent {
  dialogRef: MatDialogRef<DialogComponent> = inject(MatDialogRef);
  data: DialogData = inject(MAT_DIALOG_DATA);
  input = this.data.prefilledInput || '';
  copied = false;
  passwordErrors: string[] = [];
  svgIcon: string | undefined = this.data.svgIcon;
  isPasswordVisible = false;
  protected readonly testIds = DialogTestIds;
  private readonly passwordService = inject(PasswordService);

  onConfirm(): void {
    this.dialogRef.close(true);
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }

  isConfirmAllowed(): boolean {
    if (!this.data.needInput) return true;

    if (this.data.requireCopy && !this.copied) {
      return false;
    }

    if (this.data.showPasswordInput) {
      return this.passwordErrors.length === 0 && this.input.trim().length > 0;
    }

    if (!this.data.validationText) {
      return this.input.trim().length > 0;
    }

    return this.input.trim() === this.data.validationText;
  }

  togglePasswordVisibility(): void {
    this.isPasswordVisible = !this.isPasswordVisible;
  }

  onInputChange(value: string): void {
    this.input = value;

    if (this.data.showPasswordInput) {
      this.passwordErrors = this.passwordService.checkPassword(value);
    }
  }

  generatePassword(): void {
    this.input = this.passwordService.generatePassword();
  }

  async copyToClipboard(): Promise<void> {
    await copyToClipboard(this.input);
    this.copied = true;
  }
}
